import { useState, useEffect, useRef, createElement } from "react";
import * as runtime from "react/jsx-runtime";
import { MDXContent, MDXComponents } from "mdx/types";
import { EvaluateOptions, evaluate } from "@mdx-js/mdx";
import { AnimatePresence, motion } from "framer-motion";
import Twemoji from "react-twemoji";

import { ScopeType } from "sockapi/scope-types";
import { getIsTeamConditionSatisfied } from "sockapi/scope-specs/team-spec";
import { KeyframeData } from "sockapi/scope-specs/keyframe-spec";
import { useUILock } from "client-util/useUILock";
import { Spinner } from "components/Loading";
import { Paper, Metal } from "components/Material";
import TeamDisplayName from "components/TeamDisplayName";
import { clsObj } from "client-util/util";
import { useTeamId, useSockClient } from "stores/ServerInteractionStore";
import {
  useScopeStateResolvable,
  useTeamScopeState,
  useIsPuzzleSolved,
  usePuzzleData,
} from "stores/ScopeStateStore";
import {
  useCutsceneState,
  useUpdateBrowserWidePersistentState,
  makeDefaultCutsceneState,
} from "stores/BrowserWidePersistentState";
import { useNavStore } from "stores/NavStore";
import "./Cutscene.css";
import { SFX, SFXPlayer } from "client-util/useAudioEffect";

const THE_END_REDIRECT_KEYFRAME_ID = "the-end-redirect";

const cutsceneImageNameToUrl = (imageName: string): string | null => {
  if (imageName === "blank") return null;
  return `/static/images/cutscenes/${imageName}${
    imageName.includes(".") ? "" : ".png"
  }`;
};

function Face(props: { children: string }) {
  return <span style={{ whiteSpace: "nowrap" }}>{props.children}</span>;
}

function KeyframeDisplay(props: {
  keyframe: KeyframeData;
  keyframeId: string;
  dialogueElem: MDXContent;
  dialogueComponents: MDXComponents;
  onChooseDialogOption: (index: number) => void;
  onUndo?: () => void;
  onRedo?: () => void;
}) {
  const {
    keyframe,
    keyframeId,
    dialogueElem,
    dialogueComponents,
    onChooseDialogOption,
    onUndo,
    onRedo,
  } = props;
  const { dialogOptions = [null], dialogOptionsReqs, imageName } = keyframe;
  const teamScopeState = useTeamScopeState();

  const [playedRockLobster, setPlayedRockLobster] = useState(false);
  if (imageName === "concert-cats") {
    if (!playedRockLobster) {
      setPlayedRockLobster(true);
      SFXPlayer.play(SFX["ROCK-FLOPSTER"]);
    }
  } else if (playedRockLobster) {
    setPlayedRockLobster(false);
  }

  if (teamScopeState === null) return <Spinner inline={true} />;
  const dialogOptionsReqsIsSatisfied =
    dialogOptionsReqs !== undefined
      ? dialogOptionsReqs.map((req) =>
          getIsTeamConditionSatisfied(req, teamScopeState)
        )
      : undefined;
  const imageUrl = cutsceneImageNameToUrl(imageName);

  return (
    <div className="keyframe">
      <AnimatePresence mode="popLayout">
        <motion.div
          className="keyframe-image"
          key={imageName}
          style={{
            backgroundImage: imageUrl === null ? undefined : `url(${imageUrl})`,
          }}
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />
      </AnimatePresence>
      {imageName === "kero" || imageName === "angry-kero" ? (
        <div className="keyframe-bottom-older">
          <div className="keyframe-dir-button">
            {onUndo !== undefined ? <button onClick={onUndo}>◀</button> : null}
          </div>
          <div className="cutscene-dialog-box">
            <div className="cutscene-speaker">
              <img
                className="cutscene-speaker-img-limit-height"
                src={imageUrl ?? undefined}
              />
            </div>
            <div className="cutscene-dialog-main">
              <div className="cutscene-dialogue">
                {createElement(dialogueElem, {
                  components: dialogueComponents,
                })}
              </div>
              <div className="cutscene-dialog-options">
                {dialogOptions.map((dialogOption, index) => {
                  const isDisabled = !(
                    dialogOptionsReqsIsSatisfied?.[index] ?? true
                  );
                  return (
                    <div
                      className="cutscene-dialog-option"
                      key={dialogOption}
                      onClick={(evt) => {
                        evt.preventDefault();
                        if (isDisabled) return;
                        onChooseDialogOption(index);
                      }}
                      style={isDisabled ? { cursor: "wait" } : {}}
                    >
                      <div>{dialogOption ?? "..."}</div>
                      <div>▶</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="keyframe-dir-button">
            {onRedo !== undefined ? <button onClick={onRedo}>▶</button> : null}
          </div>
        </div>
      ) : imageName === "stelle" || imageName === "stelle-happy" ? (
        <div className="keyframe-bottom-old">
          <div className="keyframe-dir-button">
            {onUndo !== undefined ? <button onClick={onUndo}>◀</button> : null}
          </div>
          <div className="keyframe-dialog-box">
            <div className="keyframe-dialogue">
              {createElement(dialogueElem, {
                components: dialogueComponents,
              })}
            </div>
            {dialogOptions.map((dialogOption, index) => {
              const isDisabled = !(
                dialogOptionsReqsIsSatisfied?.[index] ?? true
              );
              return (
                <a
                  key={`${keyframeId}_${index}`}
                  href="#"
                  className={clsObj({
                    "keyframe-dialog-option": true,
                    disabled: isDisabled,
                  })}
                  onClick={(evt) => {
                    evt.preventDefault();
                    if (isDisabled) return;
                    onChooseDialogOption(index);
                  }}
                >
                  {dialogOption ?? "..."}
                </a>
              );
            })}
          </div>
          <div className="keyframe-dir-button">
            {onRedo !== undefined ? <button onClick={onRedo}>▶</button> : null}
          </div>
        </div>
      ) : (
        <div className="keyframe-bottom">
          <div className="keyframe-dir-button">
            {onUndo === undefined ? null : (
              <a
                href="#"
                onClick={(evt) => {
                  evt.preventDefault();
                  onUndo();
                }}
              >
                <Metal rotate={0} seed="KeyframeDisplay: undo button">
                  <Twemoji>👈</Twemoji>
                </Metal>
              </a>
            )}
          </div>
          <div className="keyframe-dialog-box">
            <Paper className="keyframe-dialogue" seed={keyframe.puzName}>
              {createElement(dialogueElem, {
                components: dialogueComponents,
              })}
            </Paper>
            {dialogOptions.map((dialogOption, index) => {
              const isDisabled = !(
                dialogOptionsReqsIsSatisfied?.[index] ?? true
              );
              return (
                <a
                  key={`${keyframeId}_${index}`}
                  href="#"
                  className={clsObj({
                    "keyframe-dialog-option": true,
                    disabled: isDisabled,
                  })}
                  onClick={(evt) => {
                    evt.preventDefault();
                    if (isDisabled) return;
                    onChooseDialogOption(index);
                  }}
                >
                  {dialogOption === null ? (
                    <Metal rotate={0} seed={keyframe.puzName} active>
                      <Twemoji>💬</Twemoji>
                    </Metal>
                  ) : (
                    <Paper rotate={0} seed={dialogOption}>
                      <Twemoji>{dialogOption}</Twemoji>
                    </Paper>
                  )}
                </a>
              );
            })}
          </div>
          <div className="keyframe-dir-button">
            {onRedo === undefined ? null : (
              <a
                href="#"
                onClick={(evt) => {
                  evt.preventDefault();
                  onRedo();
                }}
              >
                <Metal rotate={0} seed="KeyframeDisplay: redo button">
                  <Twemoji>👉</Twemoji>
                </Metal>
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function Cutscene(props: { puzName: string; firstKeyframeId: string }) {
  const { puzName, firstKeyframeId } = props;
  const cutsceneState = useCutsceneState(puzName);
  const isPuzzleSolved = useIsPuzzleSolved(puzName);
  // Buffer the shown keyframe to prevent showing a loading spinner
  // every time we load a new keyframe from the server.
  const [shownKeyframe, setShownKeyframe] = useState<{
    keyframeId: string;
    keyframe: KeyframeData;
    dialogueElem: MDXContent;
    dialogueComponents: MDXComponents;
  } | null>(null);
  // When needEndCutscene is set, wait for the cutscene to be marked
  // solved from the server before processing end cutscene nav actions.
  const [needEndCutscene, setNeedEndCutscene] = useState(false);
  const updateBrowserWidePersistentState =
    useUpdateBrowserWidePersistentState();

  const { history: cutsceneHistory, historyPosition: cutsceneHistoryPosition } =
    cutsceneState;
  const browserWideKeyframeId =
    cutsceneHistoryPosition !== null
      ? (cutsceneHistory[cutsceneHistoryPosition] ?? null)
      : null;
  const [requestedKeyframeId, setRequestedKeyframeId] = useState(
    browserWideKeyframeId
  );
  const keyframeScopeState = useScopeStateResolvable(
    ScopeType.KEYFRAME,
    requestedKeyframeId === null ? null : { keyframeId: requestedKeyframeId }
  );
  const teamId = useTeamId();
  const sockClient = useSockClient();
  const setRedirectCommand = useNavStore((state) => state.setRedirectCommand);
  const setRedirectRoute = useNavStore((state) => state.setRedirectRoute);
  const advanceLock = useUILock();
  const puzData = usePuzzleData(puzName);
  const cutsceneImages = puzData?.cutsceneImages ?? null;
  const cutsceneImagesPreloadRef = useRef<HTMLImageElement[]>([]);
  // Use the browser wide state directly so that the button doesn't
  // show just before requestedKeyframeId gets updated.
  const showReplayCutscene =
    isPuzzleSolved !== null && isPuzzleSolved && browserWideKeyframeId === null;

  useEffect(() => {
    if (showReplayCutscene) setShownKeyframe(null);
  }, [showReplayCutscene, setShownKeyframe]);

  // Setting the browser-wide keyframeId races with the normal react
  // effect evaluation flow, since it broadcasts a broadcast channel
  // update packet that is immediately processed by the same tab.
  // To prevent infinite effect loops, force it back into the flow
  // by passing it through an intermediate value.
  useEffect(() => {
    setRequestedKeyframeId(browserWideKeyframeId);
  }, [browserWideKeyframeId]);

  // If it's the first time the user is seeing the cutscene,
  // redirect to the first keyframe.
  useEffect(() => {
    if (requestedKeyframeId !== null) return;
    if (isPuzzleSolved === null) return;
    if (isPuzzleSolved) return;
    updateBrowserWidePersistentState({
      cutscenesState: {
        [puzName]: {
          history: [firstKeyframeId],
          historyPosition: 0,
        },
      },
    });
  }, [
    puzName,
    requestedKeyframeId,
    firstKeyframeId,
    isPuzzleSolved,
    updateBrowserWidePersistentState,
  ]);

  useEffect(() => {
    if (requestedKeyframeId !== THE_END_REDIRECT_KEYFRAME_ID) return;
    setRedirectRoute({ route: "victory", replace: true });
  }, [
    requestedKeyframeId,
    setRedirectRoute,
    puzName,
    firstKeyframeId,
    updateBrowserWidePersistentState,
  ]);

  useEffect(() => {
    if (requestedKeyframeId === null) return;
    if (keyframeScopeState === null) return;

    const { dialogue } = keyframeScopeState;
    let isCanceled = false;
    // There seems to be an incompatibility with MDX and our React
    // version's JSX runtime type, but explicitly casting seems to
    // be fine.
    evaluate(dialogue, runtime as EvaluateOptions)
      .then((module) => {
        if (isCanceled) return;
        setShownKeyframe({
          keyframeId: requestedKeyframeId,
          keyframe: keyframeScopeState,
          dialogueElem: module.default,
          dialogueComponents: {
            Face,
            TeamDisplayName,
            Twemoji,
          },
        });
      })
      .catch((err) => {
        console.error(err);

        if (isCanceled) return;
        setShownKeyframe(null);
      });

    return () => {
      isCanceled = true;
    };
  }, [requestedKeyframeId, keyframeScopeState]);

  useEffect(() => {
    if (!needEndCutscene) return;

    // shownKeyframe should never be null here, but in case of
    // race conditions, just abort after releasing the lock.
    if (shownKeyframe === null) {
      setNeedEndCutscene(false);
      return;
    }

    const { endCutscene = false, redirectPath } = shownKeyframe.keyframe;

    // It takes a bit of time for the solve to register with the server
    // the first time the cutscene is finished. Wait for the solve
    // before performing the end cutscene actions.
    if (endCutscene && !(isPuzzleSolved ?? false)) return;

    setNeedEndCutscene(false);

    if (redirectPath !== undefined) setRedirectCommand({ path: redirectPath });
    updateBrowserWidePersistentState({
      cutscenesState: {
        [puzName]: makeDefaultCutsceneState(),
      },
    });
  }, [
    puzName,
    needEndCutscene,
    shownKeyframe,
    updateBrowserWidePersistentState,
    setRedirectCommand,
    isPuzzleSolved,
  ]);

  useEffect(() => {
    if (cutsceneImages === null) return;
    cutsceneImagesPreloadRef.current = cutsceneImages.flatMap((imgName) => {
      const imgUrl = cutsceneImageNameToUrl(imgName);
      if (imgUrl === null) return [];
      const img = new Image();
      img.src = imgUrl;
      return [img];
    });
  }, [cutsceneImages]);

  if (teamId === null || isPuzzleSolved === null)
    return <Spinner inline={true} />;

  // This branch should only be taken if this is not the first time
  // the user has seen the cutscene, or the user has just exited
  // the cutscene.
  if (isPuzzleSolved && browserWideKeyframeId === null)
    return (
      <a
        href="#"
        className="keyframe-replay"
        onClick={(evt) => {
          evt.preventDefault();
          updateBrowserWidePersistentState({
            cutscenesState: {
              [puzName]: {
                history: [firstKeyframeId],
                historyPosition: 0,
              },
            },
          });
        }}
      >
        <Paper rotate={0}>Replay cutscene</Paper>
      </a>
    );

  if (
    shownKeyframe === null ||
    // If this is null, then we will either redirect (if the puzzle
    // is solved), show the replay button (if browserWideKeyframeId
    // is set), or set requestedKeyframeId to browserWideKeyframeId..
    requestedKeyframeId === null ||
    requestedKeyframeId === THE_END_REDIRECT_KEYFRAME_ID ||
    cutsceneHistoryPosition === null
  )
    return <Spinner inline={true} />;
  const { keyframeId, keyframe, dialogueElem, dialogueComponents } =
    shownKeyframe;
  const prevHistoryPosition =
    cutsceneHistoryPosition > 0 ? cutsceneHistoryPosition - 1 : null;
  const nextHistoryPosition =
    cutsceneHistoryPosition < cutsceneHistory.length - 1
      ? cutsceneHistoryPosition + 1
      : null;

  return (
    <KeyframeDisplay
      keyframe={keyframe}
      keyframeId={keyframeId}
      dialogueElem={dialogueElem}
      dialogueComponents={dialogueComponents}
      onChooseDialogOption={(index) => {
        advanceLock.acquireAndRun(async (getIsCanceled) => {
          const endCutscene = keyframe.endCutscene ?? false;
          const needChooseDialogOption =
            (keyframe.dialogOptions ?? []).length > 1;
          const needSetCutsceneComplete = endCutscene && !isPuzzleSolved;
          // Send a step if needed.
          if (needSetCutsceneComplete || needChooseDialogOption) {
            const resp = await sockClient.sendScopeStepAndWaitAsync(
              {
                type: ScopeType.TEAM_CUTSCENES,
                teamId,
              },
              {
                setCutsceneComplete: needSetCutsceneComplete
                  ? puzName
                  : undefined,
                chooseDialogOption: needChooseDialogOption
                  ? {
                      keyframeId,
                      index,
                    }
                  : undefined,
              }
            );
            if (
              getIsCanceled() ||
              resp === null ||
              (resp.isDisallowed ?? false)
            )
              return;
          }
          if (endCutscene)
            updateBrowserWidePersistentState({
              completedStoryBeats: { [puzName]: true },
            });
          if (endCutscene || keyframe.redirectPath !== undefined) {
            setNeedEndCutscene(true);
            return;
          }
          // If there is only one nextId, then all dialog options should
          // default to that nextId.
          const nextKeyframeId =
            keyframe.nextId.length === 1
              ? keyframe.nextId[0]
              : keyframe.nextId[index];
          if (nextKeyframeId === undefined)
            throw new Error(
              `no next keyframe id for option ${index} in ${keyframeId}`
            );
          updateBrowserWidePersistentState({
            cutscenesState: {
              [puzName]: {
                history: [
                  ...cutsceneHistory.slice(0, cutsceneHistoryPosition + 1),
                  nextKeyframeId,
                ],
                historyPosition: cutsceneHistoryPosition + 1,
              },
            },
          });
        });
      }}
      onUndo={
        prevHistoryPosition !== null
          ? () => {
              updateBrowserWidePersistentState({
                cutscenesState: {
                  [puzName]: {
                    history: cutsceneHistory,
                    historyPosition: prevHistoryPosition,
                  },
                },
              });
            }
          : undefined
      }
      onRedo={
        nextHistoryPosition !== null
          ? () => {
              updateBrowserWidePersistentState({
                cutscenesState: {
                  [puzName]: {
                    history: cutsceneHistory,
                    historyPosition: nextHistoryPosition,
                  },
                },
              });
            }
          : undefined
      }
    />
  );
}
